import React, { useEffect, useState } from "react";
import { ApplicationUser } from "../entities/ApplicationUser";
import { AuthenticationService } from "../services/AuthenticationService";
import { GTag } from "../utilities/Tracking/GoogleAnalytics";
import { useAppConfig } from "../utilities/useAppConfig";
import { useLocalStorage } from "../utilities/useLocalStorage";
import { useOfflineStatus } from "../utilities/useOfflineStatus";
import { UserContext } from "../utilities/useUser";
import { ProfitWell } from "../utilities/Tracking/ProfitWell"
import { LoadingPage } from "./LoadingPage";

interface UserProviderProps {}

export function UserProvider(props: React.PropsWithChildren<UserProviderProps>) {
	const isOffline = useOfflineStatus();
	const [appConfig] = useAppConfig();
	const { trackingId: mikesGoogleAnalyticsTrackingId, isEnabled: mikesGoogleAnalyticsEnabled } = appConfig.mikesGoogleAnalytics;

	const [offlineUser, setOfflineUser] = useLocalStorage<ApplicationUser | null>("offlineUser", null);
	const [onlineUser, setOnlineUser] = useState<ApplicationUser | null | undefined>(undefined);
	const [loading, setLoading] = useState(true);

	React.useEffect(() => {
		async function loadUser() {
			const result = await AuthenticationService.getAuthenticatedUser();
			if (result.success && result.data) {
				const user = result.data;
				ProfitWell.onUserAuthenticated(user.email);
				setOnlineUser(user);
				setOfflineUser(user);
			} else {
				ProfitWell.onAnonymousUser();
				setOnlineUser(null);
			}
			setLoading(false)
		}

		if (!isOffline) {
			loadUser();
		}
		if(isOffline)
			setLoading(false)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOffline]);

	useEffect(() => {
		if (onlineUser && mikesGoogleAnalyticsEnabled && !loading) {
			GTag.setUserId(onlineUser.id.toString(), mikesGoogleAnalyticsTrackingId);
		}
	}, [onlineUser, mikesGoogleAnalyticsTrackingId, mikesGoogleAnalyticsEnabled]);

	const updateUser = (user: ApplicationUser | null) => {
		setOnlineUser(user);
		if (user) {
			setOfflineUser(user);
		}
	};

	return (
		<UserContext.Provider
			value={{
				onlineUser,
				offlineUser,
				updateUser,
			}}
		>
			{loading ? <LoadingPage/> : props.children}
		</UserContext.Provider>
	);
}
